import React, { useState, useEffect, useContext, forwardRef, useImperativeHandle } from 'react';
import {
  Button,
  useToast,
  Skeleton,
  FormControl,
  FormLabel,
  Input,
  FormErrorMessage
} from '@chakra-ui/react';
import { Select } from 'chakra-react-select';
import { Controller, useForm } from 'react-hook-form';

import { useSharedExperiencesApi } from '@/shared-experiences/lib/useSharedExperiencesApi';
import { AuthContext } from '@/auth/lib/authContext';
import { useParams } from 'react-router-dom';
import { PersonaPageContext } from '../lib/personaPageContext';

const AddSharedExperienceToPersonaModalForm = forwardRef((props, ref) => {
  const {
    hideSave,
    onSubmitted,
    onSubmitting,
    personaId: propPersonaId,
    onCreateNew,
    groupId,
    onValidityChange
  } = props;
  
  const { id: urlPersonaId } = useParams();
  const personaId = propPersonaId || urlPersonaId;
  const { profile } = useContext(AuthContext);
  const { refreshSharedExperienceList, refreshExperienceList } = useContext(PersonaPageContext);
  const toast = useToast();
  const sharedExperiencesApi = useSharedExperiencesApi();
  const [sharedExperiences, setSharedExperiences] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newSharedExperienceName, setNewSharedExperienceName] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [nameError, setNameError] = useState('');
  const [inputValue, setInputValue] = useState('');
  
  const {
    handleSubmit,
    errors,
    control,
    formState: { isSubmitting, isValid }
  } = useForm();

  useImperativeHandle(ref, () => ({
    click: () => {
      if (showCreateForm) {
        handleCreateSharedExperience();
      } else {
        handleSubmit(handleAddSharedExperience)();
      }
    }
  }));

  useEffect(() => {
    fetchAvailableSharedExperiences();
  }, []);

  useEffect(() => {
    if (onValidityChange) {
      onValidityChange(isValid);
    }
  }, [isValid, onValidityChange]);

  const fetchAvailableSharedExperiences = async () => {
    try {
      setLoading(true);
      const data = await sharedExperiencesApi.fetchAvailableSharedExperiencesForUser(profile.id);
      
      let availableData = data || [];
      if (!groupId) {
        const linkedSharedExperiences = await sharedExperiencesApi.fetchSharedExperiencesByPersonaId(personaId);
        const linkedSharedExperienceIds = linkedSharedExperiences.map(item => item.shared_experiences_id);
        availableData = availableData.filter(sharedExperience => 
          !linkedSharedExperienceIds.includes(sharedExperience.id)
        );
      }
      
      const options = availableData.map(sharedExperience => ({
        label: sharedExperience.name,
        value: sharedExperience.id
      }));
      
      if (onCreateNew) {
        options.unshift({
          label: '+ Create New Shared Experiences',
          value: -1,
        });
      }
      setSharedExperiences(options);
    } catch (error) {
      console.error('Error fetching available shared experiences:', error);
      toast({
        title: 'Error',
        description: 'Failed to load available shared experiences',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAddSharedExperience = async (data) => {
    if (!data.sharedExperience) {
      toast({
        title: 'Error',
        description: 'Please select a shared experiences',
        status: 'error',
        duration: 3000,
      });
      return;
    }
    try {
      if (onSubmitting) {
        onSubmitting();
      }
      setIsAdding(true);
      await sharedExperiencesApi.addSharedExperienceToPersona(data.sharedExperience.value, personaId, groupId);
      
      await Promise.all([
        refreshSharedExperienceList(),
        refreshExperienceList()
      ]);

      if (onSubmitted) {
        onSubmitted();
      }
    } catch (error) {
      console.error('Error adding shared experience:', error);
      toast({
        title: 'Error',
        description: 'Failed to add shared experience',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setIsAdding(false);
    }
  };

  const handleCreateSharedExperience = async () => {
    if (!newSharedExperienceName.trim()) {
      setNameError('Name is required');
      return;
    }
    try {
      if (onSubmitting) {
        onSubmitting();
      }
      setIsCreating(true);
      const newSharedExperience = await sharedExperiencesApi.createSharedExperience({ name: newSharedExperienceName });
      if (newSharedExperience && newSharedExperience.id) {
        if (onSubmitted) {
          onSubmitted();
        }
      }
    } catch (error) {
      console.error('Error creating shared experiences:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to create shared experiences',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setIsCreating(false);
    }
  };

  const validateName = (name) => {
    if (!name.trim()) {
      setNameError('Name is required');
      return false;
    }
    setNameError('');
    return true;
  };



  return (
    <form onSubmit={handleSubmit(showCreateForm ? handleCreateSharedExperience : handleAddSharedExperience)}>
      {showCreateForm ? (
        <FormControl isRequired isInvalid={!!nameError} mb={3}>
          <FormLabel>Shared Experiences Name</FormLabel>
          <Input
            value={newSharedExperienceName}
            onChange={(e) => {
              setNewSharedExperienceName(e.target.value);
              validateName(e.target.value);
            }}
            placeholder="Enter shared experiences name"
          />
          <FormErrorMessage>{nameError}</FormErrorMessage>
        </FormControl>
      ) : (
        <Controller
          control={control}
          name="sharedExperience"
          rules={{ required: "Please select a shared experiences" }}
          render={({
            field: { onChange, onBlur, value, name, ref },
            fieldState: { invalid, error }
          }) => (
            <FormControl isInvalid={errors && errors.sharedExperience} mb={3}>
              <FormLabel>Select a shared experiences</FormLabel>
              {!sharedExperiences && (
                <Skeleton w={"100%"} height={"30px"}/>
              )}
              {sharedExperiences && (
                <Select
                  options={sharedExperiences}
                  menuPortalTarget={document.body}
                  styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                  onInputChange={(e) => {
                    setInputValue(e);
                    if (sharedExperiences && sharedExperiences.length > 0) {
                      const newSharedExperienceList = Array.from(sharedExperiences);
                      if (newSharedExperienceList[0]) {
                        newSharedExperienceList[0].label = e ? `+ Create New Shared Experiences "${e}"` : '+ Create New Shared Experiences';
                        setSharedExperiences(newSharedExperienceList);
                      }
                    }
                  }}
                  onChange={(e) => {
                    if (e.value === -1) {
                      if (onCreateNew) {
                        onCreateNew(inputValue);
                      } else {
                        setNewSharedExperienceName(inputValue);
                        setShowCreateForm(true);
                      }
                    } else {
                      onChange(e);
                    }
                  }}
                  onBlur={onBlur}
                  value={value}
                />
              )}
              <FormErrorMessage>{errors && errors.sharedExperience}</FormErrorMessage>
            </FormControl>
          )}
        />
      )}
      <Button
        style={{
          display: hideSave ? 'none' : 'inherit',
        }}
        ref={ref}
        isLoading={isAdding || isCreating}
        disabled={!isValid}
        type='submit'
        my={2}
      >Save</Button>
    </form>
  );
});

AddSharedExperienceToPersonaModalForm.displayName = 'AddSharedExperienceToPersonaModalForm';
export default AddSharedExperienceToPersonaModalForm; 